import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faCalendarCheck, faCalendarDay, faUserPlus, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchDashboardStats } from '../../store/clinics/clinicsSlice';
import { Link, useNavigate } from 'react-router-dom';
import BtnTaratic from '../../components/ui/BtnTaratic';
import ClinicsNewPatientModal from './ClinicsNewPatientModal';
import ClinicsNewAppointmentModal from './ClinicsNewAppointmentModal';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: any;
  colorClass: string;
  iconColor?: string;
  glowClass?: string;
}> = ({ title, value, icon, colorClass, iconColor = 'text-white', glowClass = 'icon-box-glow-cyan' }) => (
  <div data-ui="clinics-dashboard-page-region-001" className="p-5 rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md flex flex-col gap-3 relative overflow-hidden group transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] border-none">
    <div data-ui="clinics-dashboard-page-region-002" className={`absolute inset-0 bg-gradient-to-br ${colorClass} opacity-20 group-hover:opacity-35 transition-opacity duration-300 pointer-events-none`} />
    <div data-ui="clinics-dashboard-page-region-003" className="flex items-center gap-3 relative z-10">
      <div data-ui="clinics-dashboard-page-region-004" className={`w-10 h-10 rounded-xl flex items-center justify-center ${glowClass} group-hover:scale-110 transition-transform duration-300`}>
        <FontAwesomeIcon icon={icon} className={`text-lg ${iconColor} icon-inner-glow-white`} />
      </div>
      <div data-ui="clinics-dashboard-page-region-005" className="text-sm font-medium text-white">{title}</div>
    </div>
    <div data-ui="clinics-dashboard-page-region-006" className="relative z-10 flex flex-col">
      <span className="text-3xl font-bold text-white tracking-tight">
        {value}
      </span>
    </div>
  </div>
);

const ClinicsDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dashboardStats, loading } = useAppSelector((state) => state.clinics);

  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchDashboardStats());
  }, [dispatch]);

  const stats = dashboardStats?.stats;
  const recentPatients = dashboardStats?.recent_patients || [];

  return (
    <div data-ui="clinics-dashboard-page" className="page-theme-surface page-theme-clinics flex flex-col h-full w-full p-8 gap-8 overflow-y-auto custom-scrollbar relative">
      {/* HEADER & ACTION BUTTONS */}
      <div data-ui="clinics-dashboard-page-region-007" className="flex flex-wrap items-center justify-between gap-4 relative z-10">
        <div data-ui="clinics-dashboard-page-region-008" className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-white tracking-wide">{t('clinics.dashboard.title')}</h1>
          <p className="text-white font-medium">
            Resumen general y métricas clave de tu clínica
          </p>
        </div>
        <div data-ui="clinics-dashboard-page-region-009" className="flex items-center gap-3 flex-wrap">
          <BtnTaratic
            onClick={() => setIsPatientModalOpen(true)}
            color="blue"
            size="md"
            className="flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faUserPlus} className="icon-inner-glow-white" />
            <span className="whitespace-nowrap">{t('clinics.dashboard.newPatient')}</span>
          </BtnTaratic>
          <BtnTaratic
            onClick={() => setIsAppointmentModalOpen(true)}
            color="blue"
            size="md"
            className="flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faCalendarCheck} className="icon-inner-glow-white" />
            <span className="whitespace-nowrap">{t('clinics.dashboard.newAppointment')}</span>
          </BtnTaratic>
        </div>
      </div>

      {loading && !stats ? (
        <div data-ui="clinics-dashboard-page-region-010" className="flex justify-center items-center py-12">
          <div data-ui="clinics-dashboard-page-region-011" className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
        </div>
      ) : (
        <>
          {/* STATS CARDS GRID */}
          <div data-ui="clinics-dashboard-page-region-012" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 relative z-10">
            <StatCard
              title={t('clinics.dashboard.activePatients')}
              value={stats?.active_patients || 0}
              icon={faUsers}
              colorClass="from-blue-500 to-cyan-500"
              iconColor="text-cyan-400"
              glowClass="icon-box-glow-cyan"
            />
            <StatCard
              title={t('clinics.dashboard.patientsToday')}
              value={stats?.new_patients_today || 0}
              icon={faUserPlus}
              colorClass="from-green-500 to-emerald-500"
              iconColor="text-emerald-400"
              glowClass="icon-box-glow-emerald"
            />
            <StatCard
              title={t('clinics.dashboard.appointmentsToday')}
              value={stats?.appointments_today || 0}
              icon={faCalendarDay}
              colorClass="from-purple-500 to-pink-500"
              iconColor="text-purple-400"
              glowClass="icon-box-glow-purple"
            />
            <StatCard
              title={t('clinics.dashboard.pendingAppointments')}
              value={stats?.pending_appointments || 0}
              icon={faCalendarCheck}
              colorClass="from-yellow-500 to-orange-500"
              iconColor="text-yellow-400"
              glowClass="icon-box-glow-yellow"
            />
          </div>

          {/* RECENT PATIENTS TABLE CONTAINER */}
          <div data-ui="clinics-dashboard-page-region-013" className="rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md overflow-hidden relative z-10 flex flex-col">
            <div data-ui="clinics-dashboard-page-region-014" className="p-5 border-b border-white/10 flex justify-between items-center">
              <span className="text-lg font-bold text-white">{t('clinics.dashboard.recentPatients')}</span>
              <Link to="/clinics/patients" className="text-xs sm:text-sm font-medium text-blue-400 hover:text-blue-300 flex items-center gap-1.5 transition-colors">
                {t('clinics.dashboard.viewAll')} <FontAwesomeIcon icon={faArrowRight} className="text-xs icon-inner-glow-white" />
              </Link>
            </div>

            {recentPatients.length === 0 ? (
              <div data-ui="clinics-dashboard-page-region-015" className="p-8 text-center text-sm text-white/60">
                {t('clinics.dashboard.noRecentPatients')}
              </div>
            ) : (
              <div data-ui="clinics-dashboard-page-region-016" className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm text-left">
                  <thead className="text-[11px] sm:text-xs text-white/60 bg-white/5 uppercase">
                    <tr>
                      <th className="px-5 py-3 font-medium">{t('clinics.dashboard.table.patient')}</th>
                      <th className="px-5 py-3 font-medium">{t('clinics.dashboard.table.status')}</th>
                      <th className="px-5 py-3 font-medium">{t('clinics.dashboard.table.diagnosis')}</th>
                      <th className="px-5 py-3 font-medium">{t('clinics.dashboard.table.lastVisit')}</th>
                      <th className="px-5 py-3 font-medium text-right">{t('clinics.dashboard.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {recentPatients.map((patient) => (
                      <tr key={patient.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-5 py-3.5 font-medium text-white whitespace-nowrap">{patient.full_name}</td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${patient.status === 'active' ? 'bg-green-500/20 border-green-500/30 text-green-300' :
                            patient.status === 'inactive' ? 'bg-orange-500/20 border-orange-500/30 text-orange-300' :
                              'bg-gray-500/20 border-gray-500/30 text-gray-300'
                            }`}>
                            {patient.status === 'active' ? t('clinics.dashboard.status.active') : patient.status === 'inactive' ? t('clinics.dashboard.status.inactive') : t('clinics.dashboard.status.discharged')}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-white/80 max-w-[200px] truncate">{patient.diagnosis || '—'}</td>
                        <td className="px-5 py-3.5 text-white/80 whitespace-nowrap">
                          {patient.last_visit ? new Date(patient.last_visit).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-5 py-3.5 text-right whitespace-nowrap">
                          <Link to={`/clinics/patients/${patient.id}`}>
                            <BtnTaratic color="ghost-dark" size="xs" className="px-3 py-1 text-xs">
                              {t('clinics.dashboard.table.viewProfile')}
                            </BtnTaratic>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {isPatientModalOpen && (
        <ClinicsNewPatientModal
          onClose={() => setIsPatientModalOpen(false)}
          onSuccess={() => {
            setIsPatientModalOpen(false);
            dispatch(fetchDashboardStats());
            navigate('/clinics/patients');
          }}
        />
      )}

      {isAppointmentModalOpen && (
        <ClinicsNewAppointmentModal
          onClose={() => setIsAppointmentModalOpen(false)}
          onSuccess={() => {
            setIsAppointmentModalOpen(false);
            dispatch(fetchDashboardStats());
          }}
        />
      )}
    </div>
  );
};

export default ClinicsDashboardPage;
